import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import { Form, FormDescription } from '@/components/ui/form';
import { AppEquipmentTypeMultiSelectField, AppImageField, AppInputField, AppSelectField } from '@/form/FormUI';
import { RetireEquipmentTypeDialog } from '@/app/itemType/RetireEquipmentTypeDialog';
import { useAsyncAtomValue, useLoadableAtomValue } from '@/state/loadableHooks';
import { extractFieldErrors } from '@/util/extractFieldErrors';
import { zodResolver } from '@hookform/resolvers/zod';
import type { EquipmentType, EquipmentTypeId, RoomType, SpaceId } from '@rock/types';
import { EquipmentTypeSchemaV2, RoomGroups, RoomTypeLabels } from '@rock/types';
import { CircleX, PlusCircleIcon } from 'lucide-react';
import { type FC, useCallback, useState } from 'react';
import { type FieldErrors, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';
import { equipmentTypeState, equipmentTypesState, spacesState } from '../../state';

interface EquipmentTypeFormProps {
    /** The space a NEW type lands in by default (an existing `type` brings its own). */
    spaceId: SpaceId | undefined;
    loading: boolean;
    type: EquipmentType | null;
    /**
     * Pre-fills the NEW-type defaults (ignored when editing an existing `type`) — the promotion flow
     * seeds title/icon/group/scope from an equipment-type candidate; the admin reviews and edits
     * everything, including the still-editable id.
     */
    seed?: Partial<EquipmentType>;
    onSave: (type: EquipmentType) => void;
    /** Discards a type that was never published. A published type is retired instead. */
    onDelete: () => void;
    onRetired?: () => void;
    onCancel: () => void;
}

const EquipmentTypeForm: FC<EquipmentTypeFormProps> = ({
    spaceId,
    loading,
    type,
    seed,
    onSave,
    onDelete,
    onRetired,
    onCancel,
}) => {
    const form = useForm<z.input<typeof EquipmentTypeSchemaV2>, unknown, EquipmentType>({
        resolver: zodResolver(EquipmentTypeSchemaV2),
        defaultValues: type || {
            object: 'equipment-type',
            schema: 2,
            images: [],
            spaceId: spaceId ?? '',
            id: '',
            title: '',
            subtitle: '',
            group: '',
            order: 1,
            ...seed,
        },
    });

    const watched = useWatch({ control: form.control });

    // Everything space-scoped in here follows the FIELD, not the prop — picking a space in the form
    // has to re-aim the system-parts picker and the image storage path with it. An unset space reads
    // as '', which matches no type and yields an empty (but harmless) list.
    const spaces = useAsyncAtomValue(spacesState, []);
    const formSpaceId = (watched?.spaceId ?? '') as SpaceId;
    const equipmentTypes = useAsyncAtomValue(equipmentTypesState(formSpaceId), []);

    // Retired parts stay in: the multi-select leaves them out of its options but names the chosen ones.
    const eligibleSystemParts = equipmentTypes.filter((t) => t.scope !== 'system' || t.id === type?.id);

    const systemParts = useFieldArray({
        control: form.control,
        name: 'systemParts',
    });
    const selectedSystemPartsData = systemParts.fields.map((f) => ({
        fieldId: f.id,
        equipmentTypeId: f.typeId as EquipmentTypeId | '[context]',
    }));

    function selectSystemPart(typeId: EquipmentTypeId) {
        if (!systemParts.fields.some((f) => f.typeId === typeId)) {
            systemParts.append({ typeId: typeId });
        }
    }

    function removeSystemPart(index: number) {
        systemParts.remove(index);
    }

    const images = useFieldArray({
        control: form.control,
        name: 'images',
    });

    const onInvalid = useCallback((errors: FieldErrors) => {
        toast.error('Validation Errors', {
            description: extractFieldErrors(errors).join(', '),
        });
    }, []);

    // `SpaceIdSchema` is an unconstrained branded string, so the resolver lets an empty space through
    // — and a type with no space is filtered out of every list that groups by it.
    const handleSave = useCallback(
        (type: EquipmentType) => {
            if (!type.spaceId) {
                toast.error('Pick a space for this equipment type');
                return;
            }
            onSave(type);
        },
        [onSave],
    );

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSave, onInvalid)}>
                <div className="py-4">
                    <h6>Details</h6>
                    <div className="flex flex-col gap-4 items-stretch p-4">
                        <AppSelectField
                            label="Space"
                            placeholder="Select a space"
                            control={form.control}
                            name="spaceId"
                            description="Which space this type belongs to (not editable after create)."
                            options={spaces.map((space) => ({ value: space.id, label: space.name }))}
                            disabled={!!type}
                        />
                        <AppInputField
                            label="ID"
                            placeholder="Unique identifier (not editable after create)"
                            control={form.control}
                            name="id"
                            disabled={!!type}
                        />
                        <AppSelectField
                            label="Equipment Scope"
                            control={form.control}
                            name="scope"
                            options={['normal', 'system', 'component', 'supplies']}
                            description="How this equipment is used.  Systems are guide-only equipment (no actual equipment created of this type). Components are equipment that can only be added to a System. Supplies are consumable items like filters."
                        />
                        {watched?.scope === 'system' && (
                            <AppEquipmentTypeMultiSelectField
                                label="System Parts"
                                description="Select the parts that are eligible to be added to this system."
                                equipmentTypes={eligibleSystemParts}
                                removeEquipmentType={removeSystemPart}
                                selectedEquipmentTypesData={selectedSystemPartsData}
                                selectEquipmentType={selectSystemPart}
                            />
                        )}
                        <AppInputField label="Title" control={form.control} name="title" placeholder="Title" />
                        <AppInputField
                            label="Short Title"
                            control={form.control}
                            name="shortTitle"
                            placeholder="Short Title"
                            description="Used instead of Title when short on space (Optional)"
                        />
                        <AppInputField
                            label="Subtitle"
                            control={form.control}
                            name="subtitle"
                            placeholder="Subtitle"
                            description="Displayed under the title when the user is selecting Equipment, further clarifying or providing examples of this type of equipment."
                        />
                        <AppInputField
                            label="Icon"
                            control={form.control}
                            name="icon"
                            description="Icon List: https://lucide.dev/icons"
                        />
                        <div className="flex flex-col gap-2">
                            <AppSelectField
                                control={form.control}
                                name="roomGroupHint"
                                label="Assumed Room Group"
                                placeholder="None"
                                description="When selecting a room during a guide, the room list will be pre-filtered to this group."
                                options={RoomGroups.map((group) => ({ value: group.id, label: group.label }))}
                            />
                            {watched?.roomGroupHint && (
                                <div className="flex flex-row justify-end">
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => form.setValue('roomGroupHint', undefined)}
                                    >
                                        Clear Assumed Room Group
                                    </Button>
                                </div>
                            )}
                        </div>
                        <div className="flex flex-col gap-2">
                            <AppSelectField
                                control={form.control}
                                name="autoSelectRoomType"
                                label="Auto-Select Room Type"
                                placeholder="None"
                                description="When selecting a room during a guide, if exactly one room of this type exists, it will be auto-selected."
                                options={(Object.entries(RoomTypeLabels) as [RoomType, string][]).map(
                                    ([value, label]) => ({
                                        value,
                                        label,
                                    }),
                                )}
                            />
                            {watched?.autoSelectRoomType && (
                                <div className="flex flex-row justify-end">
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => form.setValue('autoSelectRoomType', undefined)}
                                    >
                                        Clear Auto-Select Room Type
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="py-4">
                    <h6>Placement</h6>
                    <div className="flex flex-col gap-4 items-stretch p-4">
                        <AppInputField label="Group" placeholder="Group Name" control={form.control} name="group" />
                        <AppInputField label="Order" control={form.control} name="order" inputType="number" />
                    </div>
                </div>
                {watched?.id && formSpaceId && (
                    <div className="py-4">
                        <h6>Images</h6>
                        <div className="flex flex-col gap-4 items-stretch p-4 group/images">
                            <FormDescription>Default equipment images for this equipment type.</FormDescription>
                            {images.fields.map((imageField, imageIndex) => (
                                <div key={imageField.id} className="flex flex-row items-start gap-3 group/image">
                                    <div className="flex-1">
                                        <AppImageField
                                            form={form}
                                            name={`images.${imageIndex}`}
                                            storagePathPrefix={`spaces/${formSpaceId}/equipment-types/${watched.id}/images`}
                                            label={
                                                imageIndex === 0 ? 'Default Equipment Image' : `Image ${imageIndex + 1}`
                                            }
                                            usesFullImageSchema={true}
                                        />
                                    </div>
                                    <CircleX
                                        className="cursor-pointer opacity-0 max-md:opacity-100 group-hover/image:opacity-100 transition-opacity fill-white hover:fill-red-300 mt-8"
                                        onClick={() => images.remove(imageIndex)}
                                    />
                                </div>
                            ))}
                            <div className="text-center">
                                <Button
                                    type="button"
                                    onClick={() =>
                                        images.append({ id: '', url: '', width: 0, height: 0, thumbnail: '' })
                                    }
                                    variant="ghost"
                                    size="sm"
                                    className="cursor-pointer h-6 text-xs opacity-0 max-md:opacity-100 group-hover/images:opacity-100 transition-opacity"
                                >
                                    <PlusCircleIcon /> Add Image
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
                <DialogFooter className="flex flex-row items-center justify-between sm:justify-between">
                    {type ? (
                        <RemoveTypeButton type={type} loading={loading} onDiscard={onDelete} onRetired={onRetired} />
                    ) : (
                        <div />
                    )}
                    <div className="flex flex-row items-center justify-center gap-2">
                        <Button variant="secondary" onClick={onCancel} disabled={loading} type="button">
                            Cancel
                        </Button>
                        <Button type="submit" disabled={loading}>
                            {type ? 'Update Equipment Type' : 'Create Equipment Type'}
                        </Button>
                    </div>
                </DialogFooter>
            </form>
        </Form>
    );
};

/**
 * A published type is retired (reversible, and everything naming it keeps resolving); one that never
 * left its draft is discarded outright.
 */
const RemoveTypeButton: FC<{
    type: EquipmentType;
    loading: boolean;
    onDiscard: () => void;
    onRetired?: () => void;
}> = ({ type, loading, onDiscard, onRetired }) => {
    const [live, liveLoadable] = useLoadableAtomValue(equipmentTypeState(type.id));
    const [confirming, setConfirming] = useState(false);

    // The retire stamps live before it moves anything, so an open dialog outlives `retiredAt` until the call returns.
    if (liveLoadable.state !== 'hasData' || (live?.retiredAt && !confirming)) return <div />;
    if (live) {
        return (
            <>
                <Button type="button" variant="destructive" onClick={() => setConfirming(true)} disabled={loading}>
                    Retire
                </Button>
                <RetireEquipmentTypeDialog
                    type={live}
                    open={confirming}
                    onOpenChange={setConfirming}
                    onRetired={onRetired}
                />
            </>
        );
    }
    return (
        <>
            <Button type="button" variant="destructive" onClick={() => setConfirming(true)} disabled={loading}>
                Discard Draft
            </Button>
            <AlertDialog open={confirming} onOpenChange={setConfirming}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Discard Draft: {type.title}</AlertDialogTitle>
                        <AlertDialogDescription>
                            This type was never published. Discarding removes it for good and cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="cursor-pointer" disabled={loading}>
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction variant="destructive" onClick={onDiscard} disabled={loading}>
                            Discard
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

export default EquipmentTypeForm;
